import React from "react"
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { CII_RATING_COLORS } from "@/features/voyagePlanner/fuelRequestParams"
import {
  WW_BLUE,
  PANEL_TEXT_MUTED,
  PANEL_TEXT_FAINT,
  PANEL_CARD_BG,
  PANEL_CARD_BORDER,
  PANEL_DIVIDER,
} from "@/theme/tokens"
import StatusBadge, { STATUS_BADGE_VARIANTS } from "@/components/ui/StatusBadge"
import { PRIORITIES, PRIORITY_LABELS, buildOptionCards, defaultPriority } from "./optionCardModel.jsx"

dayjs.extend(utc)

const T = {
  ink: WW_BLUE,
  muted: PANEL_TEXT_MUTED,
  faint: PANEL_TEXT_FAINT,
  disabled: "#b9c5d0",
  hairline: PANEL_DIVIDER,
  track: "#e9eef3",
  good: "#2e7d32",
  goodBg: "rgba(46, 125, 50, 0.10)",
  bad: "#d64545",
  badBg: "rgba(214, 69, 69, 0.10)",
}

const CHIP_TONE = {
  better: { bg: T.goodBg, fg: T.good },
  worse: { bg: T.badBg, fg: T.bad },
  neutral: { bg: T.hairline, fg: T.faint },
}

const TABULAR = { fontVariantNumeric: "tabular-nums" }
const UPPER_SX = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.4px",
  textTransform: "uppercase",
  lineHeight: 1.3,
}
const BARS_HINT =
  "Green = saving, red = loss vs. the active route, scaled per dimension to the largest difference among the compared routes."

function DeltaChip({ delta }) {
  if (!delta) return null
  const tone = CHIP_TONE[delta.tone] ?? CHIP_TONE.neutral
  return (
    <Box
      component="span"
      sx={{
        ...TABULAR,
        px: 0.75,
        py: "1px",
        borderRadius: "8px",
        fontSize: 10.5,
        fontWeight: 700,
        whiteSpace: "nowrap",
        backgroundColor: tone.bg,
        color: tone.fg,
      }}
    >
      {delta.label}
    </Box>
  )
}

function SavingsBars({ card }) {
  return (
    <Tooltip title={BARS_HINT} arrow enterDelay={600}>
      <Box sx={{ mt: 1.25, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}>
        {PRIORITIES.map((dim) => {
          const value = card.isBaseline ? 0 : card.bars[dim]
          const has = value !== 0
          const half = Math.round(Math.abs(value) * 50)
          return (
            <Box key={dim}>
              <Typography sx={{ fontSize: 10, lineHeight: 1.4, color: has || card.reference ? T.muted : T.disabled }}>
                {PRIORITY_LABELS[dim]}
                {card.reference && (
                  <Box component="span" sx={{ ...TABULAR, ml: 0.5, fontWeight: 600, color: T.ink }}>
                    {card.reference[dim]}
                  </Box>
                )}
              </Typography>
              <Box sx={{ position: "relative", mt: "3px", height: 3, borderRadius: "2px", backgroundColor: T.track }}>
                {has && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: value > 0 ? "50%" : `${50 - half}%`,
                      width: `${half}%`,
                      borderRadius: "2px",
                      backgroundColor: value > 0 ? T.good : T.bad,
                    }}
                  />
                )}
                {!card.isBaseline && (
                  <Box sx={{ position: "absolute", top: -1, bottom: -1, left: "50%", width: "1px", backgroundColor: T.disabled }} />
                )}
              </Box>
            </Box>
          )
        })}
      </Box>
    </Tooltip>
  )
}

const HEADLINE_COLOR = { good: T.good, bad: T.bad, neutral: T.ink }

// "TIME SAVED vs. [■ Nordsee-01 ACTIVE]": label row naming the baseline route.
// Fixed row height: cards with and without the "vs." chip keep bars and rows aligned.
const LABEL_ROW_HEIGHT = 22
const HEADLINE_MIN_HEIGHT = 54

function HeadlineLabel({ label, color, card }) {
  return (
    <Box sx={{ height: LABEL_ROW_HEIGHT, display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
      <Typography sx={{ ...UPPER_SX, color, flexShrink: 0 }}>{label}</Typography>
      {card.comparedTo && (
        <>
          <Typography sx={{ fontSize: 11.5, color: T.muted, flexShrink: 0 }}>vs.</Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              minWidth: 0,
              px: 0.75,
              py: "2px",
              borderRadius: "8px",
              backgroundColor: "#f3f6f9",
              border: `1px solid ${PANEL_CARD_BORDER}`,
            }}
          >
            <Box
              sx={{ width: 8, height: 8, borderRadius: "2px", backgroundColor: STATUS_BADGE_VARIANTS.active.fg, flexShrink: 0 }}
            />
            <Typography
              sx={{ fontSize: 13, lineHeight: 1.2, fontWeight: 700, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {card.comparedTo}
            </Typography>
            <StatusBadge variant="active" label="Active" />
          </Box>
        </>
      )}
    </Box>
  )
}

function Headline({ card }) {
  if (!card.headline) {
    if (card.isBaseline) {
      return (
        <Box sx={{ minHeight: HEADLINE_MIN_HEIGHT }}>
          <HeadlineLabel label="Active route" color={T.faint} card={card} />
        </Box>
      )
    }
    const sub = !card.savings
      ? "no active route to compare with"
      : Object.values(card.savings).some((v) => v != null)
        ? "no gain"
        : "not comparable"
    return (
      <Box sx={{ minHeight: HEADLINE_MIN_HEIGHT }}>
        <HeadlineLabel label="No saving" color={T.faint} card={card} />
        <Box sx={{ mt: 0.75, display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 26, height: 3, borderRadius: "2px", backgroundColor: T.disabled }} />
          <Typography sx={{ fontSize: 11.5, color: T.faint }}>{sub}</Typography>
        </Box>
      </Box>
    )
  }
  const color = HEADLINE_COLOR[card.headline.tone] ?? T.good
  return (
    <Box sx={{ minHeight: HEADLINE_MIN_HEIGHT }}>
      <HeadlineLabel label={card.headline.label} color={color} card={card} />
      <Box sx={{ mt: 0.25, display: "flex", alignItems: "baseline", gap: 1, minWidth: 0 }}>
        <Typography
          sx={{ ...TABULAR, fontSize: 26, fontWeight: 700, color, letterSpacing: "-.02em", lineHeight: 1.15 }}
        >
          {card.headline.value}
        </Typography>
        {card.headline.sub && (
          <Typography sx={{ ...TABULAR, fontSize: 11.5, color: T.muted, whiteSpace: "nowrap" }}>
            {card.headline.sub}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

function DetailRow({ row }) {
  const valueColor = row.ciiRating ? (CII_RATING_COLORS[row.ciiRating] ?? T.ink) : T.ink
  const value = (
    <Box component="span" sx={{ ...TABULAR, fontWeight: 600, color: valueColor, textAlign: "right" }}>
      {row.value}
    </Box>
  )
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, fontSize: 12.5, lineHeight: 1.5 }}>
      <Typography sx={{ fontSize: 12.5, color: T.muted, flexShrink: 0 }}>{row.label}</Typography>
      <Box sx={{ ml: "auto", display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
        {row.tooltip ? (
          <Tooltip title={row.tooltip} arrow>
            {value}
          </Tooltip>
        ) : (
          value
        )}
        <DeltaChip delta={row.delta} />
      </Box>
    </Box>
  )
}

function CardHeader({ card, isCalculating }) {
  const variant = STATUS_BADGE_VARIANTS[card.kind] ?? STATUS_BADGE_VARIANTS.active
  const calcTs = card.computedAt ?? null
  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: "2px", backgroundColor: variant.fg, flexShrink: 0 }} />
        <Tooltip title={card.vesselName ? `${card.name} · ${card.vesselName}` : card.name} arrow>
          <Typography
            sx={{ fontSize: 14, fontWeight: 700, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {card.name}
          </Typography>
        </Tooltip>
        {card.isSuggested && isCalculating && (
          <CircularProgress size={12} thickness={5} sx={{ color: variant.fg, flexShrink: 0 }} />
        )}
        <Box sx={{ ml: "auto", flexShrink: 0 }}>
          <StatusBadge variant={card.kind} label={card.pill.label} />
        </Box>
      </Box>
      {calcTs ? (
        <Typography sx={{ ...TABULAR, mt: 0.25, pl: "16px", fontSize: 10.5, color: T.faint, whiteSpace: "nowrap" }}>
          {dayjs.utc(calcTs).format("DD MMM HH:mm")} UTC
        </Typography>
      ) : null}
    </>
  )
}

function OptionCard(props) {
  const { card } = props
  return (
    <Box
      sx={{
        flex: "1 1 280px",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        p: 1.75,
        borderRadius: "10px",
        backgroundColor: PANEL_CARD_BG,
        border: `1px solid ${PANEL_CARD_BORDER}`,
      }}
    >
      <CardHeader {...props} />
      <Box sx={{ mt: 1.25 }}>
        <Headline card={card} />
      </Box>
      <SavingsBars card={card} />
      <Box sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${T.hairline}`, display: "grid", gap: "3px", alignContent: "start", flex: 1 }}>
        {card.rows.map((row) => (
          <DetailRow key={row.key} row={row} />
        ))}
      </Box>
    </Box>
  )
}

// Summary tab: one option card per compared route, savings vs. the active
// route in time, fuel and warnings; the planner objective picks the recommended card.
export default function RouteOptionCards({
  scrollRef,
  summaries,
  isCalculating,
}) {
  const { cards } = React.useMemo(
    () => buildOptionCards(summaries, defaultPriority(summaries)),
    [summaries],
  )

  return (
    <Box data-tour="table-cards" sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
          pr: 0.5,
          "&::-webkit-scrollbar": { width: 6, height: 6 },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0, 79, 125, 0.25)", borderRadius: 3 },
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "stretch", gap: 1.5 }}>
          {cards.map((card) => (
            <OptionCard
              key={card.id}
              card={card}
              isCalculating={isCalculating}
            />
          ))}
        </Box>
      </Box>
    </Box>
  )
}
